<template>
    <div>
        <div class="flex flex-col">
            <button @click="$router.push('/cari')"
                class="flex mb-4 px-4 py-2 space-x-2  text-sm bg-gray-200 hover:bg-gray-300 text-black dark:bg-gray-700  dark:text-gray-100    rounded shadow w-40">
                <ArrowLeftIcon class="w-5 h-5" />
                <span>
                    Tüm Borçlar
                </span>
            </button>
        </div>
        <h1 class="text-xl font-bold mb-1">{{ driverName || 'Bilinmeyen' }}</h1>
        <p class="text-sm text-gray-500 mb-4">Sürücüye ait borç ve ödeme hareketleri</p>

        <div class="flex flex-wrap gap-2 items-end mb-4">
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Başlangıç Tarihi
                </label>
                <input type="date" v-model="startDate"
                    class="h-10 px-3 rounded-lg border border-light-border dark:border-gray-600 bg-primary-bg dark:bg-gray-800 text-sm text-primary-text dark:text-gray-100 focus:border-indigo-500 focus:ring focus:ring-blue-100/50" />
            </div>
            <div>
                <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Bitiş Tarihi
                </label>
                <input type="date" v-model="endDate"
                    class="h-10 px-3 rounded-lg border border-light-border dark:border-gray-600 bg-primary-bg dark:bg-gray-800 text-sm text-primary-text dark:text-gray-100 focus:border-indigo-500 focus:ring focus:ring-blue-100/50" />
            </div>
            <button @click="applyDateFilter"
                class="bg-primary-button hover:bg-primary-button-hover text-white px-4 py-2 rounded text-sm">
                Filtrele
            </button>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div class="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-light-border dark:border-dark-border">
                <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Kalan Borç</h3>
                <p class="text-2xl font-bold text-red-600">{{ formatPrice(remainingDebt) }} ₺</p>
            </div>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-light-border dark:border-dark-border">
                <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Toplam Borç</h3>
                <p class="text-2xl font-bold text-blue-600">{{ formatPrice(totalDebtAmount) }} ₺</p>
            </div>
            <div class="bg-white dark:bg-gray-800 p-4 rounded-lg shadow border border-light-border dark:border-dark-border">
                <h3 class="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Ödenen Borç</h3>
                <p class="text-2xl font-bold text-green-600">{{ formatPrice(totalPaidAmount) }} ₺</p>
            </div>
        </div>
        <div
            class="overflow-hidden rounded-md border border-light-border dark:border-dark-border bg-primary-bg dark:bg-primary-dark-bg  text-primary-text dark:text-gray-100  pb-3 shadow-md">
            <div class="w-full overflow-x-auto">
                <table class="min-w-full text-left text-sm text-secondary-text dark:text-gray-300">
                    <thead class=" bg-gray-100 dark:bg-gray-800 font-medium">
                        <tr>
                            <th class="p-4">Tarih</th>
                            <th class="p-4">Tutar</th>
                            <th class="p-4">Tür</th>
                            <th class="p-4">Açıklama</th>
                        </tr>
                    </thead>
                    <tbody class="border-b transition hover:light-hover dark:hover:dark-hover">
                        <tr v-for="b in booklets" :key="b.booklet_id" class="border-t">
                            <td class="p-4">{{ formatDate(b.date) }}</td>
                            <td class="p-4">{{ b.amount.toFixed(2) }} ₺</td>
                            <td class="p-4">
                                <span :class="b.is_payment ? 'text-green-600' : 'text-red-600'">
                                    {{ b.is_payment ? 'Ödeme' : 'Borç' }}
                                </span>
                            </td>
                            <td class="p-4">{{ b.description || '-' }}</td>
                        </tr>

                        <tr v-if="booklets.length === 0">
                            <td colspan="4" class="text-center py-6 text-gray-500 dark:text-gray-400">
                                Herhangi bir kayıt bulunamadı.
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <AppPagination 
              :currentPage="currentPage" 
              :totalPages="totalPages" 
              :pageSize="pageSize"
              :totalItems="totalResults"
              @prev-page="currentPage--"
              @next-page="currentPage++" 
              @go-to-page="page => currentPage = page"
              @update:pageSize="handlePageSizeChange"
            />
        </div>
    </div>
</template>

<script>
import { getApi } from "@/services/api";
import { ArrowLeftIcon } from '@heroicons/vue/24/outline';
import AppPagination from "@/components/AppPagination.vue";
import { DateTime } from "luxon";

export default {
    name: "DriverBooks",
    components: {
        ArrowLeftIcon,
        AppPagination
    },
    data() {
        return {
            booklets: [],
            driverName: "",
            currentPage: 1,
            pageSize: 50,
            totalResults: 0,
            startDate: DateTime.now().minus({ months: 1 }).toFormat("yyyy-MM-dd"),
            endDate: DateTime.now().toFormat("yyyy-MM-dd"),
            remainingDebt: 0,
            totalDebtAmount: 0,
            totalPaidAmount: 0,
        };
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalResults / this.pageSize);
        },
        totalDebt() {
            return this.booklets
                .filter(b => !b.is_payment)
                .reduce((sum, b) => sum + b.amount, 0);
        },
        totalPayment() {
            return this.booklets
                .filter(b => b.is_payment)
                .reduce((sum, b) => sum + b.amount, 0);
        }
    },
    methods: {
        formatPrice(value) {
            if (value === null || value === undefined) return '0,00';
            const numValue = parseFloat(value);
            if (isNaN(numValue)) return '0,00';
            const parts = numValue.toFixed(2).split('.');
            const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ".");
            const decimalPart = parts[1];
            return `${integerPart},${decimalPart}`;
        },

        formatDate(dateStr) {
            if (!dateStr) return "-";
            return DateTime.fromISO(dateStr).toFormat("dd.MM.yyyy HH:mm");
        },

        async fetchDriverName(driverId) {
            const response = await getApi(`/drivers/${driverId}`);
            this.driverName = response?.driver_name || "Bilinmeyen";
        },

        async fetchBooklets() {
            const driverId = this.$route.params.driverId;
            const take = this.pageSize;
            const skip = (this.currentPage - 1) * this.pageSize;

            const start_date = this.startDate
                ? DateTime.fromISO(this.startDate).startOf("day").toFormat("yyyy-MM-dd'T'HH:mm:ss")
                : "";

            const end_date = this.endDate
                ? DateTime.fromISO(this.endDate).endOf("day").toFormat("yyyy-MM-dd'T'HH:mm:ss")
                : "";

            const response = await getApi(`/books/driver/${driverId}/${take}/${skip}?start_date=${start_date}&end_date=${end_date}`);

            if (response && response.booklets) {
                this.booklets = response.booklets;
                this.driverName = response.driver?.driver_name || "Bilinmeyen";
                this.totalResults = response.total_result || 0;
                
                this.calculateSummary();
            } else {
                this.booklets = [];
                this.totalResults = 0;
            }
        },

        calculateSummary() {
            const totalDebts = this.booklets
                .filter(b => !b.is_payment)
                .reduce((sum, b) => sum + b.amount, 0);
            
            const totalPayments = this.booklets
                .filter(b => b.is_payment)
                .reduce((sum, b) => sum + b.amount, 0);
            
            const remainingDebt = totalDebts - totalPayments;
            
            this.totalDebtAmount = Math.abs(totalDebts);
            this.totalPaidAmount = totalPayments;
            this.remainingDebt = remainingDebt > 0 ? remainingDebt : 0;
        },

                changePage(page) {
          this.currentPage = page;
          this.fetchBooklets();
        },

        handlePageSizeChange(newPageSize) {
          this.pageSize = newPageSize;
          this.currentPage = 1;
          this.fetchBooklets();
        },

        applyDateFilter() {
            this.currentPage = 1;
            this.fetchBooklets();
        },
    },

    async mounted() {
        const driverId = this.$route.params.driverId;
        await this.fetchBooklets();
        await this.fetchDriverName(driverId);
    },

    watch: {
        currentPage() {
            this.fetchBooklets();
        },
        pageSize() {
            this.currentPage = 1;
            this.fetchBooklets();
        }
    }
};
</script>
